import { useEffect, useState, type PropsWithChildren } from 'react';
import { useQuery } from '@tanstack/react-query';
import { NavLink, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { fetchIncidents, fetchSettings } from '@app/lib/api';
import type { SyncState } from './SyncDock';
import { SearchPalette } from './SearchPalette';
import { SyncDock } from './SyncDock';
import { ToastViewport } from './ToastViewport';

const NAV_ITEMS = [
  { to: '/', label: 'Command' },
  { to: '/jobs', label: 'Jobs' },
  { to: '/intake', label: 'Intake' },
  { to: '/library', label: 'Library' },
  { to: '/incidents', label: 'Incidents' },
  { to: '/settings', label: 'Settings' }
];

const TITLES: Record<string, { title: string; kicker: string }> = {
  '/': { title: 'Today', kicker: 'Command center' },
  '/jobs': { title: 'Jobs', kicker: 'Field workspace' },
  '/intake': { title: 'Intake', kicker: 'Paste to create' },
  '/library': { title: 'Library', kicker: 'Reference cards' },
  '/incidents': { title: 'Incidents', kicker: 'Field memory' },
  '/settings': { title: 'Settings', kicker: 'Device + data' }
};

export function AppShell({
  children,
  online,
  syncState
}: PropsWithChildren<{ online: boolean; syncState: SyncState }>) {
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: fetchSettings
  });
  const { data: incidents = [] } = useQuery({
    queryKey: ['incidents', 'unresolved'],
    queryFn: () => fetchIncidents({ unresolvedOnly: true })
  });
  const routeKey = location.pathname.startsWith('/jobs') ? '/jobs' : location.pathname;
  const routeTitle = TITLES[routeKey] ?? { title: 'Today', kicker: 'Command center' };

  useEffect(() => {
    const body = document.body;
    body.classList.toggle('one-hand', Boolean(settings?.oneHandMode));
    body.classList.toggle('high-contrast', Boolean(settings?.highContrastMode));
    body.classList.toggle('sunlight-mode', Boolean(settings?.sunlightMode));
    return () => {
      body.classList.remove('one-hand', 'high-contrast', 'sunlight-mode');
    };
  }, [settings]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setSearchOpen(true);
      }
      if (event.key === 'Escape') {
        setSearchOpen(false);
      }
    };

    let touchStartY = 0;
    let touchStartX = 0;

    const handleTouchStart = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (!touch) return;
      touchStartY = touch.clientY;
      touchStartX = touch.clientX;
    };

    const handleTouchEnd = (event: TouchEvent) => {
      const touch = event.changedTouches[0];
      if (!touch) return;
      const deltaY = touch.clientY - touchStartY;
      const deltaX = Math.abs(touch.clientX - touchStartX);
      if (touchStartY < 140 && deltaY > 90 && deltaX < 80) {
        setSearchOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  return (
    <div className="app-shell">
      <aside className="shell-rail">
        <div className="brand-lockup">
          <div className="brand-mark">SC</div>
          <div className="brand-title">Gas Service Companion</div>
        </div>
        <nav className="nav-cluster" aria-label="Primary navigation">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) => (isActive ? 'nav-link nav-link--active' : 'nav-link')}
              aria-label={item.label}
            >
              <span>{item.label}</span>
              {item.to === '/incidents' && incidents.length > 0 ? <span className="nav-badge">{incidents.length}</span> : null}
            </NavLink>
          ))}
        </nav>
        <div className="rail-footnote">Offline-first • iPhone field rail</div>
      </aside>

      <div className="shell-main">
        <header className="shell-header">
          <div className="header-left">
            <div className="header-kicker">{routeTitle.kicker}</div>
            <div className="header-title">{routeTitle.title}</div>
          </div>
          <div className="shell-header-actions">
            <button type="button" className="btn btn-ghost btn-sm shell-search-btn" onClick={() => setSearchOpen(true)}>
              Search
            </button>
            <SyncDock online={online} syncState={syncState} />
          </div>
        </header>

        <motion.main
          key={location.pathname}
          className="shell-content"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.15, ease: 'easeOut' }}
        >
          {children}
        </motion.main>

        <nav className="mobile-nav" aria-label="Primary navigation">
          <button type="button" className="mobile-nav-link" onClick={() => setSearchOpen(true)} aria-label="Search everywhere">
            <span>Search</span>
          </button>
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) => (isActive ? 'mobile-nav-link mobile-nav-link--active' : 'mobile-nav-link')}
              aria-label={item.label}
            >
              <span>{item.label}</span>
              {item.to === '/incidents' && incidents.length > 0 ? <span className="nav-badge">{incidents.length}</span> : null}
            </NavLink>
          ))}
        </nav>
      </div>

      <SearchPalette open={searchOpen} onClose={() => setSearchOpen(false)} />
      <ToastViewport />
    </div>
  );
}
